import * as React from "react";
import { connect } from "common/connect";
import StoreKeys from "common/storeKeys";
import EventService from "services/EventService";
import Store from "common/store";
import {
	Button,
	Container,
	FormControl,
	MenuItem,
	Select,
	Typography,
} from "@material-ui/core";
import { SetLoading } from "common/components/Loader/Loader";
import { toast } from "react-toastify";
import { Helmet } from "react-helmet";
import { GetCurrentGameId } from "common/constants/Games";
import { changeUrl } from "common/history";
import LookupService from "services/LookupService";
import TrophyService from "services/TrophyService";
import EventCard from "./EventCard";
import { useEventManagerStyles } from "./eventManagerStyles";
import { utc } from "./utcDate";

const propKeys = {
	events: StoreKeys.ADMIN.EVENT_MANAGER.EVENTS,
	selectedGame: StoreKeys.ADMIN.EVENT_MANAGER.SELECTED_GAME,
	roles: StoreKeys.ROLES,
};

const toastError = (error, fallback) =>
	toast(error?.message?.trim() || fallback, {
		type: "error",
	});

const NOTIFY_RESULTS = {
	Sent: "Picks-open push sent.",
	NothingSent: "Nothing sent - nobody is subscribed, or push is not configured.",
	AlreadySent: "Already sent for this stage.",
	Hidden: "Event is hidden. Unhide it first.",
	NoCurrentStage: "Event has no current stage.",
};

async function loadEvents(gameId) {
	try {
		SetLoading(true, "Loading Events");
		const loaded = await EventService.getManagedEvents(gameId);
		Store.set(StoreKeys.ADMIN.EVENT_MANAGER.EVENTS, loaded);
	} catch (error) {
		toastError(error, "Failed to load events.");
	} finally {
		SetLoading(false);
	}
}

const hasEnded = (endDate) => !!endDate && new Date(`${endDate}Z`) < new Date();

function EventManagerC({ events, selectedGame, roles }) {
	const classes = useEventManagerStyles();
	const gameId = selectedGame || GetCurrentGameId();
	const isAdmin = roles?.includes("Admin");
	const [gameOptions, setGameOptions] = React.useState(null);

	React.useEffect(() => {
		LookupService.getGames().then(setGameOptions);
	}, []);

	React.useEffect(() => {
		if (!selectedGame) {
			Store.set(StoreKeys.ADMIN.EVENT_MANAGER.SELECTED_GAME, gameId);
		}
		loadEvents(gameId);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [gameId]);

	const onGameChange = (e) => {
		Store.set(
			StoreKeys.ADMIN.EVENT_MANAGER.SELECTED_GAME,
			Number(e.target.value)
		);
	};

	const saveEvent = async (event, patch) => {
		const next = {
			eventId: event.id,
			nameOverride: event.nameOverride ?? "",
			image: event.image ?? "",
			hidden: event.hidden === true,
			rewardTypeId: event.rewardTypeId ?? null,
			...patch,
		};
		try {
			SetLoading(true, "Saving");
			await EventService.updateEventDetails({
				...next,
				rewardTypeId: next.rewardTypeId === "" ? null : next.rewardTypeId,
			});
			toast("Saved.", { type: "success" });
		} catch (error) {
			toastError(error, "Save failed.");
			return false;
		} finally {
			SetLoading(false);
		}
		await loadEvents(gameId);
		return true;
	};

	const forceSync = async (eventId) => {
		try {
			SetLoading(true, "Syncing");
			await EventService.syncNow(eventId);
			toast("Sync complete.", { type: "success" });
		} catch (error) {
			toastError(error, "Sync failed - check logs.");
			return;
		} finally {
			SetLoading(false);
		}
		await loadEvents(gameId);
	};

	const rewardTrophies = async (event) => {
		const name = event.nameOverride || event.name;
		const endEventNow = !hasEnded(event.endDate);
		const confirmation = [
			endEventNow
				? `${name} is scheduled to run until ${utc(
						event.endDate
				  )} UTC. Rewarding now ends it as of now.`
				: null,
			event.trophiesAwarded > 0
				? `${name} already has ${event.trophiesAwarded} trophies. Recalculating deletes them and awards again, which unpins everyone's pinned trophies. Continue?`
				: `Reward trophies for ${name}?`,
		]
			.filter(Boolean)
			.join("\n\n");
		if (!window.confirm(confirmation)) {
			return;
		}

		try {
			SetLoading(true, "Rewarding Trophies");
			await TrophyService.giveTrophies(event.id, endEventNow);
			toast("Trophies rewarded.", { type: "success" });
		} catch (error) {
			toastError(error, "Rewarding trophies failed - check logs.");
			return;
		} finally {
			SetLoading(false);
		}
		await loadEvents(gameId);
	};

	const notifyStageOpen = async (eventId) => {
		try {
			SetLoading(true, "Sending");
			const result = await EventService.notifyStageOpen(eventId);
			toast(NOTIFY_RESULTS[result] ?? `Result: ${result}`, {
				type: result === "Sent" ? "success" : "info",
			});
		} catch (error) {
			toastError(error, "Send failed - check logs.");
			return;
		} finally {
			SetLoading(false);
		}
		await loadEvents(gameId);
	};

	const hasEvents = Array.isArray(events) && events.length > 0;

	return (
		<Container maxWidth="md" className={classes.root}>
			<Helmet>
				<title>Pickstop - Event Manager</title>
			</Helmet>
			<div className={classes.header}>
				<Typography variant="h5">Event Manager</Typography>
				<div className={classes.headerActions}>
					<FormControl className={classes.gameSelect}>
						<Select value={gameId} onChange={onGameChange} color="primary">
							{(gameOptions ?? []).map((g) => (
								<MenuItem key={g.id} value={g.id}>
									{g.name}
								</MenuItem>
							))}
						</Select>
					</FormControl>
					<Button
						variant="contained"
						color="primary"
						onClick={() => changeUrl("/admin/event/create")}
					>
						Create Event
					</Button>
				</div>
			</div>
			{hasEvents ? (
				events.map((e) => (
					<EventCard
						key={e.id}
						event={e}
						isAdmin={isAdmin}
						onSave={(patch) => saveEvent(e, patch)}
						onRewardTrophies={() => rewardTrophies(e)}
						onNotifyStageOpen={() => notifyStageOpen(e.id)}
						onForceSync={() => forceSync(e.id)}
					/>
				))
			) : (
				<Typography>There are no events for this game.</Typography>
			)}
		</Container>
	);
}

export default function EventManager(props) {
	return connect(<EventManagerC />, propKeys, props);
}
